import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import ReviewLayout from './ReviewLayout';
import CasePracticeForm, { caseStorageKeyFor, loadDraft, missingRequiredFields } from './CasePracticeForm';
import useCompletedSession from './useCompletedSession';
import { toReviewSession } from './reviewSession';
import { getConceptualization, readPreparedInstance, requestFeedback } from '../AIClients/aiClientData';
import '../AIClients/AIClients.css';

// 사례개념화 단계에 들어올 수 없을 때 이유와 돌아갈 곳을 안내한다.
function CaseStepNotice({ session, onRetry }) {
    const notices = {
        checking: { message: '면담 정보를 확인하고 있습니다.' },
        missing: { message: '사례개념화를 작성할 면담이 없습니다. 먼저 내담자를 준비하고 면담을 진행해주세요.', to: '/ai-clients', link: '내담자 준비로 이동' },
        notCompleted: { message: '면담을 마친 뒤 사례개념화를 작성할 수 있습니다.', to: '/ai-clients/interview', link: '면담으로 돌아가기' },
        error: { message: session.error, to: '/ai-clients', link: '내담자 준비로 이동' },
    };
    const notice = notices[session.status];
    return <main className="ai-clients-page review-page"><div className="ai-clients-wrap">
        {session.status === 'checking'
            ? <p role="status">{notice.message}</p>
            : <div className="ai-alert" role="alert">{notice.message} {session.status === 'error' && <button type="button" onClick={onRetry}>다시 시도</button>} <Link to={notice.to}>{notice.link}</Link></div>}
    </div></main>;
}

CaseStepNotice.propTypes = {
    session: PropTypes.shape({ status: PropTypes.string.isRequired, error: PropTypes.string }).isRequired,
    onRetry: PropTypes.func.isRequired,
};

// 면담 연습 3단계: 끝난 면담을 바탕으로 사례개념화를 작성하고 종합 피드백을 요청하는 페이지.
export default function CaseConceptualizationPractice() {
    const navigate = useNavigate();
    const [pending, setPending] = useState(false);
    const [error, setError] = useState('');
    const [saved] = useState(readPreparedInstance);
    const [session, retrySession] = useCompletedSession(saved);

    // 서버에 저장된 항목 글을 읽는다. 조회에 실패하면 브라우저 초안으로 대신한다.
    async function readSubmittedValues() {
        try {
            return (await getConceptualization(saved.id, saved.token))?.values || {};
        } catch {
            return loadDraft(caseStorageKeyFor(saved)).values;
        }
    }

    // 서버에 저장하지 않은 필수 항목이 있으면 보내기 전에 한 번 확인한다.
    async function confirmRequiredFields() {
        const missing = missingRequiredFields(await readSubmittedValues());
        if (missing.length === 0) return true;
        const labels = missing.map(field => `· ${field.label}`).join('\n');
        return window.confirm(`아직 저장하지 않은 사례개념화 필수 항목이 있습니다.\n${labels}\n\n그래도 종합 피드백을 보낼까요?`);
    }

    // 면담 전체의 종합 피드백을 요청하고 다음 단계(종합 피드백)로 이동한다.
    async function sendFeedback() {
        if (pending) return;
        setPending(true); setError('');
        try {
            if (!(await confirmRequiredFields())) return;
            const feedback = await requestFeedback(saved.id, saved.token);
            window.scrollTo(0, 0);
            navigate('/comprehensive-feedback', { state: { instance: session.instance, feedback } });
        } catch (failure) {
            setError(failure.message);
        } finally {
            setPending(false);
        }
    }

    if (session.status !== 'ready') return <CaseStepNotice session={session} onRetry={retrySession} />;
    return <ReviewLayout session={toReviewSession(session.instance, session.startedAt)}>
        <CasePracticeForm saved={saved} clientName={session.instance.client?.name} />
        {error && <div role="alert" className="ai-alert">{error}</div>}
        <div className="review-actions review-next-action">
            <Link className="review-secondary" to="/ai-clients/interview">← 면담 기록으로</Link>
            {pending && <span className="ai-small" role="status">면담을 분석하고 있습니다. 1~2분 정도 걸릴 수 있어요.</span>}
            <button type="button" className="review-primary" disabled={pending} onClick={sendFeedback}>{pending ? '분석 중…' : '종합 피드백 보내기 →'}</button>
        </div>
    </ReviewLayout>;
}
